import React, { useState, useEffect } from 'react';

interface TermsAcceptance {
    accepted: boolean;
    acceptedAt: Date | null;
    acceptedVersion: string;
    ipAddress?: string;
}

interface TermsAndConditionsProps {
    onAcceptance: (acceptance: TermsAcceptance) => void;
    onCancel?: () => void;
    isRequired?: boolean;
    termsVersion?: string;
    title?: string;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
    onAcceptance,
    onCancel,
    isRequired = true,
    termsVersion = '1.0.0',
    title = 'Terms and Conditions'
}) => {
    const [isChecked, setIsChecked] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [showError, setShowError] = useState(false);
    const [userIP, setUserIP] = useState<string>('');

    useEffect(() => {
        fetch('https://api.ipify.org?format=json')
            .then(res => res.json())
            .then(data => setUserIP(data.ip))
            .catch(() => setUserIP('unknown'));
    }, []);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        const isBottom = target.scrollHeight - target.scrollTop === target.clientHeight;
        if (isBottom && !hasScrolledToBottom) {
            setHasScrolledToBottom(true);
        }
    };

    const handleAccept = () => {
        if (isRequired && !isChecked) {
            setShowError(true);
            return;
        }

        if (isRequired && !hasScrolledToBottom) {
            alert('Please scroll to the bottom of the terms before accepting.');
            return;
        }

        const acceptance: TermsAcceptance = {
            accepted: true,
            acceptedAt: new Date(),
            acceptedVersion: termsVersion,
            ipAddress: userIP
        };

        recordAcceptance(acceptance);
        onAcceptance(acceptance);
    };

    const recordAcceptance = async (acceptance: TermsAcceptance) => {
        try {
            const response = await fetch('/api/terms/acceptance', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: localStorage.getItem('userId'),
                    ...acceptance,
                    userAgent: navigator.userAgent
                })
            });

            if (!response.ok) {
                console.error('Failed to record acceptance');
            }
        } catch (error) {
            console.error('Error recording acceptance:', error);
        }
    };

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-5">
            <div className="bg-white rounded-xl shadow-lg p-10 max-w-2xl w-full">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">{title}</h2>
                <p className="text-gray-500 text-sm mb-5">
                    Please review and accept our terms to continue
                </p>

                <div
                    className="h-[400px] overflow-y-auto border border-gray-200 rounded-lg p-5 mb-5 bg-gray-50"
                    onScroll={handleScroll}
                >
                    <TermsContent version={termsVersion} />
                </div>

                {!hasScrolledToBottom && (
                    <div className="text-center py-2 px-3 bg-blue-50 rounded-md mb-4 text-xs text-blue-600 animate-pulse">
                        ⬇️ Scroll to read full terms
                    </div>
                )}

                <div className="my-5 p-4 bg-gray-50 rounded-md">
                    <label className="cursor-pointer flex items-center gap-3 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                                setIsChecked(e.target.checked);
                                setShowError(false);
                            }}
                            className="w-5 h-5 cursor-pointer text-blue-600 focus:ring-blue-500"
                        />
                        <span>I have read and agree to the Terms and Conditions</span>
                    </label>
                </div>

                {showError && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-md mb-5 text-sm text-center border-l-4 border-red-500">
                        You must accept the terms to proceed
                    </div>
                )}

                <div className="flex gap-4 mt-5">
                    <button
                        className="flex-1 py-3 px-6 rounded-md text-base font-semibold cursor-pointer transition-all bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md"
                        onClick={handleAccept}
                    >
                        Accept & Continue
                    </button>
                    {onCancel && (
                        <button
                            className="flex-1 py-3 px-6 rounded-md text-base font-semibold cursor-pointer transition-all bg-gray-100 text-gray-700 hover:bg-gray-200"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                    )}
                </div>

                <button
                    className="block text-center mt-5 text-blue-600 bg-transparent border-none cursor-pointer text-xs underline hover:text-blue-700"
                    onClick={() => setIsModalOpen(true)}
                >
                    View previously accepted terms
                </button>
            </div>

            {isModalOpen && (
                <AcceptedTermsModal onClose={() => setIsModalOpen(false)} />
            )}
        </div>
    );
};

const TermsContent: React.FC<{ version: string }> = ({ version }) => {
    return (
        <div className="text-sm leading-relaxed text-gray-600">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Terms of Service</h3>
            <p className="mb-3 text-xs text-gray-400">Version: {version}</p>
            <p className="mb-3 text-xs text-gray-400">Last Updated: {new Date().toLocaleDateString()}</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">1. Acceptance of Terms</h4>
            <p className="mb-3">By accessing and using this platform, you agree to be bound by these Terms and Conditions...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">2. User Obligations</h4>
            <p className="mb-3">You agree to use the platform in compliance with all applicable laws and regulations...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">3. Privacy Policy</h4>
            <p className="mb-3">Your use of the platform is also governed by our Privacy Policy...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">4. Account Security</h4>
            <p className="mb-3">You are responsible for maintaining the security of your account...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">5. Intellectual Property</h4>
            <p className="mb-3">All content and materials on the platform are protected by intellectual property laws...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">6. Termination</h4>
            <p className="mb-3">We reserve the right to terminate or suspend access to our service...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">7. Limitation of Liability</h4>
            <p className="mb-3">The platform is provided "as is" without warranties of any kind...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">8. Governing Law</h4>
            <p className="mb-3">These terms shall be governed by and construed in accordance with the laws...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">9. Changes to Terms</h4>
            <p className="mb-3">We reserve the right to modify these terms at any time...</p>

            <h4 className="text-md font-semibold text-gray-700 mt-4 mb-2">10. Contact Information</h4>
            <p className="mb-3">For questions about these Terms, please contact us at legal@example.com</p>
        </div>
    );
};

const AcceptedTermsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const [acceptanceHistory, setAcceptanceHistory] = useState<any[]>([]);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                const response = await fetch('/api/terms/acceptance/history');
                const data = await response.json();
                setAcceptanceHistory(data);
            } catch (error) {
                console.error('Error fetching history:', error);
                const stored = localStorage.getItem('termsAcceptance');
                if (stored) {
                    setAcceptanceHistory([JSON.parse(stored)]);
                }
            }
        };
        fetchHistory();
    }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50" onClick={onClose}>
            <div className="bg-white rounded-xl max-w-2xl w-[90%] max-h-[80vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center p-5 border-b border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-800">Accepted Terms History</h3>
                    <button
                        className="bg-transparent border-none text-3xl cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </div>
                <div className="p-5 overflow-y-auto">
                    {acceptanceHistory.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No acceptance history found.</p>
                    ) : (
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="p-3 text-left font-semibold text-gray-600 border-b border-gray-200">Version</th>
                                    <th className="p-3 text-left font-semibold text-gray-600 border-b border-gray-200">Accepted At</th>
                                    <th className="p-3 text-left font-semibold text-gray-600 border-b border-gray-200">IP Address</th>
                                </tr>
                            </thead>
                            <tbody>
                                {acceptanceHistory.map((record, idx) => (
                                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                        <td className="p-3 text-gray-700">{record.acceptedVersion}</td>
                                        <td className="p-3 text-gray-600">{new Date(record.acceptedAt).toLocaleString()}</td>
                                        <td className="p-3 text-gray-600">{record.ipAddress}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TermsAndConditions;